import { useEffect, useState } from 'react';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

/**
 * A phone or a tablet held sideways — the one screen the editor is laid out
 * side by side on (see `ClimbEditorSplit`).
 *
 * Asked of the pointer as well as the screen because every desktop is landscape,
 * and a desktop already draws the picture wide enough to work on with the fields
 * under it. What a sideways phone lacks is height: stacked, the picture is a
 * strip and the fields are a screen away from it.
 *
 * Any picture, landscape or not: the setter turned the phone, and the page
 * follows them rather than second-guessing why.
 */
export const EDITOR_SPLIT_QUERY = '(orientation: landscape) and (pointer: coarse)';

/**
 * Whether the *screen* is sideways, which is not what `(orientation: landscape)`
 * says: the media query compares the viewport's sides, and a phone's keyboard
 * takes enough height off a portrait viewport to make it wider than tall. On an
 * iPhone that flipped the editor into the split while the hold-type dialog was
 * opening — the fields moved to another parent, the dialog was remounted shut,
 * the keyboard went away and the page flipped back. The keyboard never turns
 * the screen, so the screen's own orientation is asked as well.
 *
 * Without the Screen Orientation API (iOS before 16.4, jsdom) the media query
 * is left to decide alone, as it always did.
 */
function screenLandscape(): boolean {
  if (typeof screen === 'undefined' || !screen.orientation) return true;
  return screen.orientation.type.startsWith('landscape');
}

function useScreenLandscape(): boolean {
  const [landscape, setLandscape] = useState(screenLandscape);

  useEffect(() => {
    if (typeof screen === 'undefined' || !screen.orientation) return;
    const orientation = screen.orientation;
    const handler = () => setLandscape(screenLandscape());
    handler();
    orientation.addEventListener('change', handler);
    return () => orientation.removeEventListener('change', handler);
  }, []);

  return landscape;
}

export function useEditorSplit(): boolean {
  const viewport = useMediaQuery(EDITOR_SPLIT_QUERY);
  const screenSideways = useScreenLandscape();
  return viewport && screenSideways;
}
